import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { cn } from '@mdvip/shared/lib/utils';
import type * as React from 'react';

interface NetworkConfig {
  bg: string;
  /** FA brands glyph class, e.g. "fa-cc-visa". */
  icon: string;
}

const NETWORK_CONFIG: Record<string, NetworkConfig> = {
  VISA: { bg: '#1434CB', icon: 'fa-cc-visa' },
  MC: { bg: '#1A1A2E', icon: 'fa-cc-mastercard' },
  AMEX: { bg: '#1F72CF', icon: 'fa-cc-amex' },
  DISC: { bg: '#FF6000', icon: 'fa-cc-discover' },
};

export interface NetworkChipProps {
  kind?: 'card' | 'bank';
  /** Card network code (e.g. "VISA", "MC", "AMEX", "DISC"). Ignored for bank kind. */
  network?: string;
  className?: string;
}

/**
 * NetworkChip — 48×32 payment method chip. Card variant renders the FA brands
 * network logo on the brand color background; bank variant renders a landmark
 * icon on a neutral background. Used inside payment method row components.
 */
function NetworkChip({ kind = 'card', network, className }: NetworkChipProps) {
  const base = cn('flex h-8 w-12 shrink-0 items-center justify-center rounded-md text-white', className);

  if (kind === 'bank') {
    return (
      <div className={cn(base, 'bg-muted-foreground')}>
        <Icon name="landmark" size="sm" />
      </div>
    );
  }

  const config = NETWORK_CONFIG[network ?? ''];
  return (
    <div
      className={cn(base, 'bg-[var(--chip-bg)]')}
      style={{ '--chip-bg': config?.bg ?? 'var(--foreground)' } as React.CSSProperties}
    >
      {config ? (
        <i className={cn('fa-brands', config.icon, 'text-lg leading-none')} aria-hidden="true" />
      ) : (
        <span className="text-[10px] font-bold tracking-tight">{network}</span>
      )}
    </div>
  );
}

export { NetworkChip };
